import { type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

/**
 * Subscription lifecycle state — mirrors `VenueSubscriptionStatus` from
 * `@bravemove/types/subscription` (`'trialing' | 'active' | 'past_due' | 'cancelled'`).
 * Kept local — `@bravemove/types` is NOT a dep of `@bravemove/ui-web`. Spec source:
 * [`docs/design/screens/revenue-lib.jsx`](../../../../../docs/design/screens/revenue-lib.jsx)
 * §3 `SubStateBadge` (line 466).
 */
export type SubState = 'trialing' | 'active' | 'past_due' | 'cancelled';
export type SubStateBadgeSize = 'sm' | 'md';

export interface SubStateBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  readonly state: SubState;
  readonly size?: SubStateBadgeSize;
  /** Override the default state label. */
  readonly label?: string;
}

interface SubStateStyle {
  readonly box: string;
  readonly dot: string;
  readonly defaultLabel: string;
  /** `active` + `past_due` get a "heartbeat" pulse to signal a live billing state. */
  readonly pulse: boolean;
}

const STATE_STYLE: Record<SubState, SubStateStyle> = {
  trialing: {
    box: 'bg-info/15 text-ink border border-info/40',
    dot: 'bg-info',
    defaultLabel: 'Trialing',
    pulse: false,
  },
  active: {
    box: 'bg-success/15 text-ink border border-success/40',
    dot: 'bg-success',
    defaultLabel: 'Active',
    pulse: true,
  },
  past_due: {
    box: 'bg-warning/15 text-ink border border-warning/40',
    dot: 'bg-warning',
    defaultLabel: 'Past due',
    pulse: true,
  },
  cancelled: {
    box: 'bg-surface-2 text-ink-3 border border-hairline-2',
    dot: 'bg-ink-3',
    defaultLabel: 'Cancelled',
    pulse: false,
  },
};

const SIZE: Record<SubStateBadgeSize, { box: string; dot: string }> = {
  sm: { box: 'px-[7px] py-[2px] gap-1.5 text-[9px]', dot: 'size-[5px]' },
  md: { box: 'px-[9px] py-[4px] gap-1.5 text-[10px]', dot: 'size-[6px]' },
};

/**
 * F38 — `SubStateBadge` (shared primitive). Subscription lifecycle pill with a
 * status dot; `active` + `past_due` pulse (motion-safe) to read as live.
 *
 * @example
 *   <SubStateBadge state="active" />
 *   <SubStateBadge state="past_due" size="sm" />
 */
export function SubStateBadge({
  state,
  size = 'md',
  label,
  className,
  ...rest
}: SubStateBadgeProps) {
  const style = STATE_STYLE[state];
  const sz = SIZE[size];
  const text = label ?? style.defaultLabel;
  return (
    <span
      role="img"
      aria-label={`Subscription status: ${text}`}
      data-testid={`sub-state-badge-${state}`}
      className={cn(
        'inline-flex items-center rounded uppercase whitespace-nowrap align-middle font-mono font-bold tracking-[0.1em]',
        style.box,
        sz.box,
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-block rounded-full shrink-0',
          sz.dot,
          style.dot,
          style.pulse && 'motion-safe:animate-pulse',
        )}
      />
      <span>{text}</span>
    </span>
  );
}
